export interface Logger {
  log(...args: any[]): void;
  warn(...args: any[]): void;
  info(...args: any[]): void;
  error(...args: any[]): void;
  group(callback: () => void): void;
}

/**
 * Creates a logger that only outputs to console when a specific feature flag is enabled
 * @param {boolean} isEnabled Whether logging is enabled
 * @param {string} prefix Optional prefix for all log messages
 * @returns An object with log, warn, error, and info methods
 */
export const createConditionalLogger = (
  isEnabled: boolean,
  prefix: string = '',
): Logger => {
  const formatMessage = (message: string) =>
    prefix ? `${prefix}: ${message}` : message;

  return {
    log: (message: string, ...args: any[]) => {
      if (isEnabled) {
        console.log(formatMessage(message), ...args);
      }
    },

    warn: (message: string, ...args: any[]) => {
      if (isEnabled) {
        console.warn(formatMessage(message), ...args);
      }
    },

    error: (message: string, ...args: any[]) => {
      // Always log errors in production, but add prefix only in debug mode
      if (isEnabled) {
        console.error(formatMessage(message), ...args);
      } else {
        // In production, log with minimal information (privacy, security)
        console.error(message);
      }
    },

    info: (message: string, ...args: any[]) => {
      if (isEnabled) {
        console.info(formatMessage(message), ...args);
      }
    },

    // Add this method to conditionally group logs together
    group: (callback: () => void) => {
      if (!isEnabled) return;

      console.group(prefix);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    },
  };
};
